import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Kondition, KonditionAnlegen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, Objektliste, Objektzeile,
         useBeforeUnload, useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { formatKondition } from "../../format.js";
import type { VerlassenWaechter } from "../../router.js";
import { KEINE_ZAHL, Meldung, alsFeldwert, alsZahl, eingabeUnlesbar, feldFehlerAus,
         meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import "../Verwaltung.css";
import { KiKnopf } from "../../components/meHuellen.js";

/**
 * Der Verwaltungsbereich ZAHLUNGSKONDITIONEN.
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Konditionsliste steht LINKS im
 * Inhalt, rechts daneben die gewählte Kondition — dasselbe Muster wie bei Unternehmen und
 * Benutzer, und dasselbe, das die Spezifikation für diesen Bereich ausdrücklich vorsieht
 * („Objekt-Bereiche … Konditionen"). Vor Etappe 2 standen alle Konditionen als Kartenliste
 * im Inhalt untereinander; zwischen Etappe 2 und diesem Entscheid stand die Liste als
 * zweite Ebene in der Verwaltungs-Spalte.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * **Die Fachregeln bleiben unverändert.** Angelegt und geändert wird weiterhin im Dialog
 * (mit derselben Formatvorschau), archiviert weiterhin erst nach Rückfrage, und der
 * Versionsvergleich läuft wie bisher über die Antwort des Servers. Der Umbau ist
 * Navigation und Anordnung — die Kondition selbst weiss nichts davon.
 *
 * **Wo welcher Dialog steht.** Der ANLEGE-Dialog steht bei seinem Knopf über der Liste,
 * der ÄNDERN-Dialog und die Archiv-Rückfrage stehen bei ihren Knöpfen im Detail. Jeder
 * Dialog steht dort, wo der Knopf steht, der ihn öffnet — sonst führte ein Klick zu einer
 * Wirkung an einer Stelle, die man gerade nicht ansieht.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste wie Detail
 * stehen im Inhalt; der Spaltenblock `vws__` gehört allein dem Bereichs-Untermenü.
 */

/** Nur die vier Wege dieses Bereichs — so lässt er sich im Test mit vier vi.fn zeichnen,
 *  ohne den ganzen ApiClient nachzubauen (Muster: BereichArtikelClient). */
export type KonditionenClient = Pick<ApiClient,
  "listPaymentTermsVerwaltung" | "createPaymentTerm" | "updatePaymentTerm"
  | "archivePaymentTerm">;

/**
 * Ersatztext für den einen Fachfehler dieses Bereichs, der ohne Feldangabe ankommen könnte.
 *
 * Im Normalfall zeigt die Maske die Meldung des SERVERS — sie ist genauer (sie nennt die
 * Anzahl), und zwei Fassungen desselben Satzes liefen über die Zeit auseinander. Dieser
 * Text greift nur, wenn die 422 kein Feld mitbringt: Dann stünde sonst gar nichts da.
 */
const IN_VERWENDUNG_ERSATZ =
  "Diese Zahlungskondition ist noch bei Kundenbeziehungen hinterlegt und kann deshalb "
  + "nicht archiviert werden.";

type KonditionDialog = {
  /** null = neu anlegen. */
  id: string | null;
  version: number;
  name: string;
  netDays: string;
  discountPercent: string;
  discountDays: string;
  invoicing: Kondition["invoicing"];
  purchaseOrderRequired: boolean;
  active: boolean;
};

const LEERER_KONDITIONSDIALOG: KonditionDialog = {
  id: null, version: 0, name: "", netDays: "", discountPercent: "", discountDays: "",
  invoicing: "STANDARD", purchaseOrderRequired: false, active: true,
};

type KonditionFeld = "name" | "netDays" | "discountPercent" | "discountDays"
  | "invoicing" | "purchaseOrderRequired" | "active";

const KONDITION_FELDER: readonly KonditionFeld[] = ["name", "netDays", "discountPercent",
  "discountDays", "invoicing", "purchaseOrderRequired", "active"];

const KONDITION_BESCHRIFTUNGEN: Readonly<Record<KonditionFeld, string>> = {
  name: "Name", netDays: "Zahlungsziel (Tage)", discountPercent: "Skonto (%)",
  discountDays: "Skontofrist (Tage)", invoicing: "Rechnungsstellung",
  purchaseOrderRequired: "Bestellnummer erforderlich", active: "Aktiv (steht zur Auswahl)",
};

/** Die Ausgangslage eines Dialogs: der Stand, wie er beim Öffnen aus der Zeile entsteht
 *  (`null` = neu anlegen). Eine Stelle für zwei Zwecke — das Öffnen selbst und der
 *  Vergleich „ist etwas ungespeichert?" (2a-Kleinpunkt). Zwei getrennte Abbildungen
 *  liefen mit dem nächsten neuen Feld auseinander, und dann meldete der Browserdialog
 *  eine Änderung, die keine ist. */
function konditionsdialogAus(k: Kondition | null): KonditionDialog {
  if (k === null) return LEERER_KONDITIONSDIALOG;
  return {
    id: k.id, version: k.version, name: k.name,
    netDays: String(k.netDays),
    discountPercent: alsFeldwert(k.discountPercent),
    discountDays: alsFeldwert(k.discountDays),
    invoicing: k.invoicing, purchaseOrderRequired: k.purchaseOrderRequired,
    active: k.active,
  };
}

function konditionsdialogGleich(a: KonditionDialog, b: KonditionDialog): boolean {
  return a.id === b.id && a.version === b.version && a.name === b.name
    && a.netDays === b.netDays && a.discountPercent === b.discountPercent
    && a.discountDays === b.discountDays && a.invoicing === b.invoicing
    && a.purchaseOrderRequired === b.purchaseOrderRequired && a.active === b.active;
}

/** „verwendet von 2 Kundenbeziehungen" — die Spezifikation verlangt die Zahl sichtbar am
 *  Eintrag, denn sie ist der Grund, aus dem sich ein Eintrag nicht archivieren lässt. */
function verwendungsText(anzahl: number): string {
  if (anzahl === 0) return "von keiner Kundenbeziehung verwendet";
  return `verwendet von ${anzahl} `
    + `${anzahl === 1 ? "Kundenbeziehung" : "Kundenbeziehungen"}`;
}

/** Die Kondition in einer Zeile — dieselbe Angabe, die später am Kunden steht. */
function konditionsText(k: Kondition): string {
  return formatKondition({ netDays: k.netDays, discountPercent: k.discountPercent,
                           discountDays: k.discountDays });
}

/**
 * Die Eigenschaften des Bereichs — dieselbe Gestalt wie bei den Vorreitern der Aufgabe 2:
 * `aktiv` als Riegel gegen mitlaufende Abfragen an fremden Adressen, die Kennung aus der
 * ADRESSE, ein Rückruf für den Adresswechsel. Navigiert wird nicht hier — die Adresse kennt
 * App.tsx.
 */
export type VerwaltungKonditionenProps = {
  aktiv: boolean;
  client: KonditionenClient;
  /** Die geöffnete Kondition aus der Adresse (`/verwaltung/zahlungskonditionen/:id`). */
  konditionId: string | null;
  onOeffnen: (konditionId: string | null) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten). Weglassbar —
   *  dann fragt niemand, was für einen Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export function useVerwaltungKonditionen(
  p: VerwaltungKonditionenProps,
): VerwaltungBereichTeile {
  const { client, aktiv, konditionId } = p;

  const [zeilen, setZeilen] = useState<Kondition[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<KonditionDialog | null>(null);
  const [archivFrage, setArchivFrage] = useState<Kondition | null>(null);
  const [archivFehler, setArchivFehler] = useState<string | null>(null);
  /** Das Archivieren läuft (eigener Schreibweg ohne Formular). */
  const [archiviert, setArchiviert] = useState(false);
  /** Eine Auskunft nach „Neu laden“: Die geöffnete Kondition gibt es nicht mehr. */
  const [auskunft, setAuskunft] = useState<string | null>(null);
  // Sie gehört zu IHRER Kondition — ein Objektwechsel räumt sie (Hausregel: kein Zustand
  // überlebt den Wechsel).
  useEffect(() => { setAuskunft(null); }, [konditionId]);

  const listPaymentTerms = client.listPaymentTermsVerwaltung;

  async function laden(): Promise<void> {
    try {
      const antwort = await listPaymentTerms();
      setZeilen(antwort.items);
      setLadeFehler(null);
    } catch (fehler) {
      setLadeFehler(
        meldungAus(fehler, "Die Zahlungskonditionen konnten nicht geladen werden."));
    }
  }

  /**
   * Formularzustände der Maske (ME-Etappe 3, Aufgabe 6): `laeuft` sperrt Felder und Fuß,
   * eine 422 (auch die eigene „keine Zahl") hängt am Feld und der Fokus springt dorthin.
   * Die Kondition trägt eine Version: Eine 409 hält die Eingaben als „fremder Stand" fest,
   * „Neu laden" liest die Liste und setzt die Maske auf den Serverstand (vorher stand nur
   * die Servermeldung als Sammelmeldung da, ohne Weg nach vorn).
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Keine Zahlungskondition offen.");
      const eingabe = eingabeAus(d);
      if (d.id === null) {
        // Anlegen antwortet nur mit der Kennung — die Liste muss neu gelesen werden,
        // denn sie trägt Version und Verwendungszählung, die das Anlegen nicht liefert.
        const angelegt = await client.createPaymentTerm(eingabe);
        await laden();
        // Und die ADRESSE geht auf den frischen Satz (Gegenstück zum `onOeffnen(null)` im
        // Archivieren): Wer eben etwas angelegt hat, will es sehen — und die Kennung, die
        // das Anlegen zurückgibt, ist genau dafür da.
        p.onOeffnen(angelegt.id);
      } else {
        const neu = await client.updatePaymentTerm(d.id, d.version, eingabe);
        // Die Antwort übernehmen (neue Version) — siehe die Begründung im Benutzerbereich.
        setZeilen((alt) => (alt === null ? alt
          : alt.map((x) => (x.id === neu.id ? neu : x))));
      }
      return true;
    },
    neuLaden: async () => {
      const antwort = await listPaymentTerms();
      setZeilen(antwort.items);
      const d = dialog;
      if (d === null || d.id === null) return;
      const frisch = antwort.items.find((k) => k.id === d.id);
      if (frisch === undefined) {
        // Inzwischen archiviert oder gelöscht (Review A6): NICHT still zu „Neu anlegen“
        // werden — die Maske schliesst und sagt, was geschehen ist (Muster KundeNotizen).
        setDialog(null);
        setAuskunft("Diese Zahlungskondition wurde inzwischen archiviert oder gelöscht.");
        return;
      }
      setDialog(konditionsdialogAus(frisch));
    },
    bekannteFelder: KONDITION_FELDER,
    beschriftungen: KONDITION_BESCHRIFTUNGEN,
  });
  const feldFehler = formular.feldFehler as Partial<Record<KonditionFeld, string>>;
  /** Läuft ein Schreibweg — das Speichern der Maske oder das Archivieren? */
  const arbeitet = formular.laeuft || archiviert;

  // Der Ladeeffekt ruft `listPaymentTerms` direkt statt `laden()`: Er muss abbrechbar
  // sein (die Ansicht kann während des Ladens verschwinden), `laden()` ist es bewusst
  // nicht — es läuft nur nach einem eigenen Schreibvorgang, bei dem die Ansicht steht.
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listPaymentTerms()
      .then((antwort) => {
        if (!abgebrochen) {
          setZeilen(antwort.items);
          // Ein geglücktes Nachladen HEILT den alten Fehler (Lehre der Fixrunde 1 zu
          // Aufgabe 2): Seit die Bereiche als Hook über der ganzen Anwendung leben,
          // überlebt ihr Zustand jeden Bereichswechsel — ein Fehlerstreifen stünde sonst
          // für immer über einer Liste, die längst wieder da ist.
          setLadeFehler(null);
        }
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Zahlungskonditionen konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listPaymentTerms]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion aus
   * Aufgabe 3 der ersten Etappe): Der halb ausgefüllte Dialog, die Archiv-Rückfrage,
   * Feld- und Sammelmeldungen sind Bearbeitungszustände OHNE eigene Adresse und gehören
   * zu einer Absicht, die man verlassen hat.
   */
  useEffect(() => {
    if (aktiv) return;
    setDialog(null);
    formular.zuruecksetzen();
    setArchivFrage(null);
    setArchivFehler(null);
    // Auch der LADEFEHLER: Beim nächsten Betreten läuft die Abfrage neu — bis ihre Antwort
    // da ist, soll die Auskunft „lädt" heissen und nicht der Fehler von vorhin. Er würde
    // sonst die dreistufige Deep-Link-Auskunft im Detail verstellen.
    setLadeFehler(null);
  }, [aktiv]);

  /*
   * Änderungsdialog und Archiv-Rückfrage gehören DEM Objekt, über dem sie geöffnet wurden —
   * also fallen sie mit der ADRESSE, nicht mit dem Klick, der sie sonst allein geschlossen
   * hätte. Adressen wechseln auch ohne Klick auf einen Knopf dieser Maske: Zurück-Taste,
   * Rückweg im Kopf des Einspalters, ein Lesezeichen auf eine andere Kondition. Bliebe der
   * Dialog liegen, hinge an ihm weiter der Verlassen-Wächter und die Warnung des Browsers,
   * obwohl längst niemand mehr etwas bearbeitet.
   *
   * Der ANLEGE-Dialog (`id === null`) bleibt: Er gehört keinem Objekt, sondern dem Bereich,
   * und steht in der Liste, die bei jedem Adresswechsel innerhalb des Bereichs stehen
   * bleibt.
   */
  useEffect(() => {
    setDialog((d) => (d === null || d.id === null || d.id === konditionId ? d : null));
    setArchivFrage((f) => (f === null || f.id === konditionId ? f : null));
    // Und die Sammelmeldung mit (Fixrunde 1): Sie gehört der Kondition, über der sie
    // entstand — ein gescheitertes Speichern der einen darf nicht als Alarm über der
    // nächsten stehenbleiben. Dieselbe Regel wie beim `hinweis` in BereichArtikel.
    formular.zuruecksetzen();
  }, [konditionId]);

  // 2a-Kleinpunkt: der Browser-Standarddialog beim ECHTEN Verlassen. Verglichen wird mit
  // der Ausgangslage beim Öffnen, aus der Zeile nachgebaut — ein bloss geöffneter,
  // unberührter Dialog löst nichts aus.
  const dialogGeaendert = dialog !== null && !konditionsdialogGleich(
    dialog, konditionsdialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /**
   * OB gerade bearbeitet wird: genau dann, wenn ein Dialog dieses Bereichs offen ist und
   * etwas Ungespeichertes trägt. Er kann das nur, solange der Bereich offen ist — der
   * Effekt oben räumt ihn beim Verlassen weg, und der daneben beim Objektwechsel.
   *
   * Gebraucht wird die Auskunft für die Liste: Der Anlege-Knopf und die Objektzeilen sind
   * währenddessen gesperrt (dieselbe Regel wie in Etappe 1 — die Liste soll unter einer
   * laufenden Eingabe nichts anstossen können).
   */
  const bearbeitung = aktiv && dialogGeaendert;

  const { bestaetigen, bestaetigung } = useBestaetigung();

  /**
   * Der Verlassen-Wächter des ROUTERS (Hausmuster, siehe KundeStammdaten): Solange der
   * Dialog Ungespeichertes trägt, fragt jede Navigation nach — der Rückweg im Kopf des
   * Einspalters ebenso wie die Zurück-Taste und der Sprung auf einen anderen
   * Verwaltungsbereich.
   *
   * Bestätigtes Verlassen VERWIRFT den Dialog (Lehre der Fixrunde 1 zu Aufgabe 2): Sonst
   * sagte die Rückfrage die Unwahrheit — die Eingaben lägen weiter im Zustand und stünden
   * beim Zurückkommen wieder da.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!bearbeitung || registriereVerlassenWaechter === undefined) return;
    return registriereVerlassenWaechter(async () => {
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Diese Zahlungskondition hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) { setDialog(null); formular.zuruecksetzen(); }
      return weiter;
    });
  }, [bearbeitung, registriereVerlassenWaechter]);

  function oeffnen(k: Kondition | null): void {
    formular.zuruecksetzen();
    setAuskunft(null);
    setDialog(konditionsdialogAus(k));
  }

  /** Der Rumpf für den Schreibweg — wirft die eigene 422, wenn eine Eingabe keine Zahl
   *  ist (dieselbe Gestalt wie ein Befund des Servers, siehe `eingabeUnlesbar`). */
  function eingabeAus(d: KonditionDialog): KonditionAnlegen {
    const netDays = alsZahl(d.netDays);
    const discountPercent = alsZahl(d.discountPercent);
    const discountDays = alsZahl(d.discountDays);
    const unlesbar: { field: KonditionFeld; message: string }[] = [];
    if (netDays === KEINE_ZAHL) {
      unlesbar.push({ field: "netDays", message: "Bitte eine Zahl eintragen." });
    }
    if (discountPercent === KEINE_ZAHL) {
      unlesbar.push({ field: "discountPercent", message: "Bitte eine Zahl eintragen." });
    }
    if (discountDays === KEINE_ZAHL) {
      unlesbar.push({ field: "discountDays", message: "Bitte eine Zahl eintragen." });
    }
    if (unlesbar.length > 0) throw eingabeUnlesbar(unlesbar);
    return {
      name: d.name,
      netDays: netDays as number | null,
      discountPercent: discountPercent as number | null,
      discountDays: discountDays as number | null,
      invoicing: d.invoicing,
      purchaseOrderRequired: d.purchaseOrderRequired,
      active: d.active,
    };
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) setDialog(null);
  }

  async function archivieren(k: Kondition): Promise<void> {
    setArchivFehler(null);
    setArchiviert(true);
    try {
      await client.archivePaymentTerm(k.id, k.version);
      setArchivFrage(null);
      // Archivierte Konditionen stehen nicht mehr in der Liste — neu lesen statt raten.
      await laden();
      // Und die Adresse mit: Was archiviert wurde, gibt es in der Liste nicht mehr; das
      // Detail zeigte sonst „gibt es nicht (mehr)" über einer Handlung, die gerade geglückt
      // ist. Der Bereich bleibt offen, nur ohne gewähltes Objekt.
      p.onOeffnen(null);
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 422) {
        const { felder } = feldFehlerAus(fehler, ["usageCount"]);
        setArchivFehler(felder.usageCount
          ?? (fehler.code === "IN_VERWENDUNG" ? IN_VERWENDUNG_ERSATZ : fehler.message));
      } else {
        setArchivFehler(meldungAus(fehler, "Unerwarteter Fehler beim Archivieren."));
      }
    } finally {
      setArchiviert(false);
    }
  }

  function vorschau(d: KonditionDialog): string {
    const netDays = alsZahl(d.netDays);
    if (netDays === KEINE_ZAHL || netDays === null) {
      return "Bitte ein Zahlungsziel in Tagen eintragen.";
    }
    const prozent = alsZahl(d.discountPercent);
    const tage = alsZahl(d.discountDays);
    return formatKondition({
      netDays,
      discountPercent: prozent === KEINE_ZAHL ? null : prozent,
      discountDays: tage === KEINE_ZAHL ? null : tage,
    });
  }

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  async function abbrechen(): Promise<void> {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  /** Der Dialograhmen — für Anlegen (bei der Liste) und Ändern (im Detail) derselbe. */
  function dialogRahmen(d: KonditionDialog): ReactElement {
    return (
      <Modal
        titel={d.id === null ? "Neue Zahlungskondition" : "Zahlungskondition ändern"}
        onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
        {formular.meldung !== null && <Meldung text={formular.meldung} />}
        <Formular beschriftung="oben" {...formular.formularProps}
          hinweis={formular.zustand === "fremder-stand" ? (
            <span>
              Diese Zahlungskondition wurde inzwischen geändert. Deine Eingaben stehen noch
              da — „Neu laden" übernimmt den aktuellen Stand.
            </span>
          ) : undefined}>
          <Feld id="vw-k-name" label="Name" bearbeitbar wert={d.name}
            onChange={(w) => setDialog((alt) => (alt === null ? alt : { ...alt, name: w }))}
            {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
          <Feld id="vw-k-netDays" label="Zahlungsziel (Tage)" bearbeitbar
            wert={d.netDays}
            onChange={(w) => setDialog((alt) => (alt === null ? alt : { ...alt, netDays: w }))}
            {...(feldFehler.netDays !== undefined ? { fehler: feldFehler.netDays } : {})} />
          <Feld id="vw-k-discountPercent" label="Skonto (%)" bearbeitbar
            wert={d.discountPercent}
            onChange={(w) => setDialog((alt) => (alt === null ? alt
              : { ...alt, discountPercent: w }))}
            {...(feldFehler.discountPercent !== undefined
              ? { fehler: feldFehler.discountPercent } : {})} />
          <Feld id="vw-k-discountDays" label="Skontofrist (Tage)" bearbeitbar
            wert={d.discountDays}
            onChange={(w) => setDialog((alt) => (alt === null ? alt
              : { ...alt, discountDays: w }))}
            {...(feldFehler.discountDays !== undefined
              ? { fehler: feldFehler.discountDays } : {})} />

          <Auswahl id="vw-k-invoicing" name="vw-k-invoicing" beschriftung="Rechnungsstellung"
            wert={d.invoicing}
            {...(feldFehler.invoicing !== undefined ? { fehler: feldFehler.invoicing } : {})}
            onGeaendert={(e) => setDialog((alt) => (alt === null ? alt
              : { ...alt, invoicing: e.wert as Kondition["invoicing"] }))}>
            <option value="STANDARD">Einzelrechnung</option>
            <option value="COLLECTIVE">Sammelrechnung</option>
          </Auswahl>

          {/* Echte Ja/Nein-Angaben → `Schalter` (me-schalter) statt roher Kästchen. */}
          <Schalter id="vw-k-purchaseOrderRequired" beschriftung="Bestellnummer erforderlich"
            an={d.purchaseOrderRequired}
            {...(feldFehler.purchaseOrderRequired !== undefined
              ? { fehler: feldFehler.purchaseOrderRequired } : {})}
            onGeaendert={(e) => setDialog((alt) => (alt === null ? alt
              : { ...alt, purchaseOrderRequired: e.an }))} />
          <Schalter id="vw-k-active" beschriftung="Aktiv (steht zur Auswahl)"
            an={d.active}
            {...(feldFehler.active !== undefined ? { fehler: feldFehler.active } : {})}
            onGeaendert={(e) => setDialog((alt) => (alt === null ? alt
              : { ...alt, active: e.an }))} />
        </Formular>

        <p className="vw__vorschau">So steht es später am Kunden: {vorschau(d)}</p>

        <ModalFuss speichernText="Speichern"
          speichernGesperrt={formular.zustand !== "bereit"}
          abbrechenGesperrt={arbeitet}
          onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
      </Modal>
    );
  }

  const satz = konditionId === null
    ? null
    : (zeilen ?? []).find((k) => k.id === konditionId) ?? null;

  // ---------------------------------------------------------------------------
  // Die Objektliste — zweite Ebene unter dem Eintrag „Zahlungskonditionen"
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      {/* Der Anlege-Knopf steht ÜBER der Liste — dort, wo er im Bestand über der
          Kartenliste stand, und dort, wo ihn die Bereiche der Etappe 1 haben. */}
      <div className="vw__leiste">
        <KiKnopf art="primaer" deaktiviert={bearbeitung} onClick={() => oeffnen(null)}>
          Neue Zahlungskondition
        </KiKnopf>
      </div>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Es gibt noch keine Zahlungskondition.</p>
      )}

      <Objektliste label="Zahlungskonditionen">
        {(zeilen ?? []).map((k) => (
          <Objektzeile
            key={k.id}
            name={k.name}
            meta={konditionsText(k)}
            {...(k.active ? {} : { marken: ["stillgelegt"] })}
            gewaehlt={k.id === konditionId}
            gesperrt={bearbeitung}
            ariaLabel={`Zahlungskondition ${k.name}, ${konditionsText(k)}, anzeigen`}
            onOeffnen={() => p.onOeffnen(k.id)}
          />
        ))}
      </Objektliste>

      {/* Der Anlege-Dialog steht bei seinem Knopf, also bei der Liste (siehe Kopf). */}
      {dialog !== null && dialog.id === null && dialogRahmen(dialog)}
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — GENAU EINE Kondition
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (konditionId === null) {
      return (
        <p className="vw__hinweis">
          Links eine Zahlungskondition wählen — Zahlungsziel, Skonto, Rechnungsstellung und
          ihre Verwendung stehen dann hier.
        </p>
      );
    }
    if (satz === null) {
      /*
       * Die dreistufige Auskunft für ein Lesezeichen auf eine Kondition, die die Liste nicht
       * enthält (Etappe-1-Lektion): „steht nicht in der Liste" darf erst behauptet werden,
       * wenn es eine Liste GIBT. Solange sie lädt, wäre die Aussage falsch; ist sie
       * gescheitert, liegt es nicht an der Kondition.
       */
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <>
          {auskunft !== null && <Hinweis art="info">{auskunft}</Hinweis>}
          <p className="vw__hinweis">
            Diese Zahlungskondition gibt es nicht (mehr) — archivierte stehen nicht in der
            Liste. Bitte links eine wählen.
          </p>
        </>
      );
    }

    const k = satz;
    return (
      /* Eigene Identität je Kondition (Etappe-1-Lektion `key={objekt.id}`): Ohne diesen
         Schlüssel nähme ein Adresswechsel den DOM-Zustand dieser Karte auf die nächste
         Kondition mit. */
      <div key={k.id} className="vw__eintrag">
        {/* Die Sammelmeldung eines abgebrochenen Panels bleibt bei IHRER Kondition stehen
            (Fixrunde 1) — der Formularzustand räumt sie beim Objektwechsel. */}
        {formular.meldung !== null && dialog === null && <Meldung text={formular.meldung} />}
        <div className="vw__eintrag-kopf">
          <span className="vw__eintrag-name">{k.name}</span>
          {!k.active && <span className="vw__marke">stillgelegt</span>}
        </div>
        <span className="vw__eintrag-wert">{konditionsText(k)}</span>
        <span className="vw__eintrag-zusatz">
          {k.invoicing === "COLLECTIVE" ? "Sammelrechnung" : "Einzelrechnung"}
          {k.purchaseOrderRequired ? " · Bestellnummer nötig" : ""}
        </span>
        <span className="vw__eintrag-zusatz">{verwendungsText(k.usageCount)}</span>
        <div className="vw__eintrag-aktionen">
          <KiKnopf name={`${k.name} ändern`} onClick={() => oeffnen(k)}>
            Ändern
          </KiKnopf>
          <KiKnopf name={`${k.name} archivieren`} onClick={() => { setArchivFehler(null); setArchivFrage(k); }}>
            Archivieren
          </KiKnopf>
        </div>

        {/* Rückfrage und Änderungsdialog stehen bei ihren Knöpfen, also im Detail — und
            gehören GENAU DER Kondition, über der sie geöffnet wurden (Lehre der Fixrunde 1
            zu Aufgabe 2): Ohne diesen Vergleich überlebte die Rückfrage einen
            Adresswechsel, den dieses Detail nicht auslöst — die Zurück-Taste etwa —, und
            ihr Knopf archivierte dann die Kondition A, während B darunter stünde. */}
        {archivFrage !== null && archivFrage.id === k.id && (
          <Modal titel={`Archivieren — ${archivFrage.name}`}
            /* Auch Escape wartet auf den Server (Nachreview Fixrunde 1): Der Knopf ist
               während des Archivierens gesperrt — derselbe Riegel gilt für die Taste,
               sonst schriebe die Antwort in ein geschlossenes Panel. Er steht am RAHMEN,
               weil der die Taste hält. */
            abbrechenGesperrt={arbeitet}
            onAbbrechen={() => setArchivFrage(null)}>
            <p className="vw__hinweis">
              „{archivFrage.name}" wird stillgelegt und steht danach nicht mehr zur Auswahl.
              Bestehende Kundenbeziehungen behalten ihre Werte. Zurzeit{" "}
              {verwendungsText(archivFrage.usageCount)}.
            </p>
            {archivFehler !== null && <Meldung text={archivFehler} />}
            <ModalFuss speichernText="Ja, archivieren" speichernGesperrt={arbeitet}
              abbrechenGesperrt={arbeitet}
              onSpeichern={() => { void archivieren(archivFrage); }}
              onAbbrechen={() => { if (arbeitet) return; setArchivFrage(null); }} />
          </Modal>
        )}

        {dialog !== null && dialog.id === k.id && dialogRahmen(dialog)}
      </div>
    );
  })();

  return { liste, detail: <>{detail}{bestaetigung}</> };
}
